import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  TextField,
  InputAdornment,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpIcon,
  VideoLibrary as VideoIcon,
  Article as ArticleIcon
} from '@mui/icons-material';

interface HelpCenterProps {
  open: boolean;
  onClose: () => void;
}

interface HelpTopic {
  id: string;
  title: string;
  category: string;
  content: string;
  videoUrl?: string;
}

const helpTopics: HelpTopic[] = [
  {
    id: 'activation-progress',
    title: 'What is Activation Progress?',
    category: 'Getting Started',
    content: 'Activation Progress tracks your engagement with the platform. Complete Day-7 goals (connect site, view dashboard 2+ times, explore a pillar, complete an action) to unlock insights. Day-30 goals build on this for deeper engagement.',
    videoUrl: '/help/activation-intro'
  },
  {
    id: 'readiness-pillars',
    title: 'Understanding Site SEO Readiness Pillars',
    category: 'SEO Basics',
    content: 'Five core areas determine your SEO health: On-Page Technical SEO, Google Index Coverage, Internal Link Graph, Core Web Vitals, and Content Quality. Each pillar has specific metrics and recommended actions.',
  },
  {
    id: 'early-stage-mode',
    title: 'What is Early Stage Mode?',
    category: 'Getting Started',
    content: 'New sites start in Early Stage Mode while collecting data and establishing SEO foundation. After 30 days and meeting readiness criteria, you can transition to Performance Mode for advanced analytics.',
  },
  {
    id: 'action-queue',
    title: 'How to use Action Queue',
    category: 'Actions',
    content: 'Actions are prioritized recommendations based on your site\'s data. High priority items have the biggest impact. Mark actions complete as you implement them to track progress.',
  },
  {
    id: 'performance-transition',
    title: 'Transitioning to Performance Mode',
    category: 'Advanced',
    content: 'Transition to Performance Mode after 30+ days when readiness score is 80%+. This unlocks full analytics, custom reports, and advanced features.',
  }
];

export const HelpCenter: React.FC<HelpCenterProps> = ({ open, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTopic, setExpandedTopic] = useState<string | false>(false);

  const filteredTopics = helpTopics.filter(
    (topic) =>
      topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      topic.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      topic.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = Array.from(new Set(helpTopics.map((t) => t.category)));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400 },
          p: 3
        }
      }}
    >
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={600}>
            Help Center
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <TextField
          fullWidth
          placeholder="Search help topics..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />

        <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Quick Links
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mb={3}>
          <Chip
            icon={<VideoIcon />}
            label="Video Tutorials"
            clickable
            size="small"
            onClick={() => window.open('/help/videos', '_blank')}
          />
          <Chip
            icon={<ArticleIcon />}
            label="Documentation"
            clickable
            size="small"
            onClick={() => window.open('/help/docs', '_blank')}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {filteredTopics.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No help topics found
          </Typography>
        ) : (
          <Box>
            {categories.map((category) => {
              const categoryTopics = filteredTopics.filter((t) => t.category === category);
              if (categoryTopics.length === 0) return null;

              return (
                <Box key={category} mb={2}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                    {category}
                  </Typography>
                  {categoryTopics.map((topic) => (
                    <Accordion
                      key={topic.id}
                      expanded={expandedTopic === topic.id}
                      onChange={(_, isExpanded) => setExpandedTopic(isExpanded ? topic.id : false)}
                      elevation={0}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        mb: 1,
                        '&:before': { display: 'none' }
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2" fontWeight={500}>
                          {topic.title}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {topic.content}
                        </Typography>
                        {topic.videoUrl && (
                          <Chip
                            icon={<VideoIcon />}
                            label="Watch Tutorial"
                            size="small"
                            clickable
                            onClick={() => window.open(topic.videoUrl, '_blank')}
                          />
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default HelpCenter;
